import { useQuery } from '@tanstack/react-query';
import { getTaskQueryOptions } from 'src/services/tasks/taskQueries';
import { type RegionTask } from 'src/services/tasks/types';
import { type FeatureFlags } from 'src/store/userSlice';
import { isUpperOrWholeBodyTask, WeightRestrictionMaxRangeHandler } from 'src/utils/restrictionUtilsLegacy';

import { type ValidationTaskGroup } from './types';

interface MaxValueConfigurable {
  restrictionMaxValue?: undefined | number | null;
}
const childTaskSortComparator = (a: MaxValueConfigurable, b: MaxValueConfigurable) =>
  (a.restrictionMaxValue || 0) - (b.restrictionMaxValue || 0);

/** This hook is meant to encapsulate the complexity of supporting validation of restrictions with regardless of whether task grouping is enabled or disabled. The actual validation is in a separate utility method for cleaner dependency management and type checking */
export const useFetchTaskGroups = (
  isFeatureFlagsLoaded: boolean,
  featureFlags: FeatureFlags,
  regionId: number | undefined,
): { isLoading: boolean; taskGroups: ValidationTaskGroup[] } => {
  const waitingForDependencies = !isFeatureFlagsLoaded || regionId == undefined || regionId < 1;

  // effects must run in a stable order unconditionally. We use the enabled param to toggle which request to actually run
  const featureEnabledResponse = useQuery({
    ...getTaskQueryOptions.regionTaskGroups(regionId!),
    select: ({ taskGroups }) =>
      taskGroups.map((tg) => ({
        ...tg,
        tasks: [...tg.tasks].sort(childTaskSortComparator),
      })),
    enabled: !waitingForDependencies && !!featureFlags?.TASK_GROUPING,
  });

  const featureFlagDisabledResponse = useQuery({
    ...getTaskQueryOptions.regionTasks(regionId!),
    select: selectStaticTaskGroups,
    enabled: !waitingForDependencies && !featureFlags?.TASK_GROUPING,
  });

  if (waitingForDependencies) {
    return { isLoading: true, taskGroups: [] };
  }

  if (featureFlags.TASK_GROUPING) {
    const { data: taskGroups = [], isLoading } = featureEnabledResponse;
    return { isLoading, taskGroups };
  } else {
    const { data: taskGroups = [], isLoading } = featureFlagDisabledResponse;
    return { isLoading, taskGroups };
  }
};

/** Use the pre-existing task grouping logic to transform a list of single tasks into groups based on static logic */
const selectStaticTaskGroups = (regionTasks: RegionTask[]) => {
  if (!regionTasks.length) {
    // handle edge case as well as unit tests
    return [];
  }

  // Get list of grouped tasks
  const liftCarryTasks = regionTasks.filter((t) => t.label.startsWith('Lift/Carry'));
  const pushPullTasks = regionTasks.filter((t) => t.label.startsWith('Push/Pull') && !isUpperOrWholeBodyTask(t));

  // Parse restrictionMaxValue
  liftCarryTasks.forEach((t) => {
    t.restrictionMaxValue = t.restrictionMaxValue ?? WeightRestrictionMaxRangeHandler.getChildOption(t.label).value;
  });
  pushPullTasks.forEach((t) => {
    t.restrictionMaxValue = t.restrictionMaxValue ?? WeightRestrictionMaxRangeHandler.getChildOption(t.label).value;
  });

  // Sort
  liftCarryTasks.sort(childTaskSortComparator);
  pushPullTasks.sort(childTaskSortComparator);

  const liftCarryTaskGroup: ValidationTaskGroup = {
    tasks: liftCarryTasks,
  };
  const pushPullTaskGroup: ValidationTaskGroup = {
    tasks: pushPullTasks,
  };

  return [liftCarryTaskGroup, pushPullTaskGroup];
};
